/**
 * original source: https://ui.shadcn.com/docs/components/dialog
 */
import * as React from 'react'
import { AlertDialog as AlertDialogPrimitive } from 'radix-ui'
import { cn } from '@joyful/utils'
import { Button } from './Button'
import type { ButtonProps } from './Button'
import type { ComponentProps } from 'react'

export type AlertDialogProps = React.ComponentProps<
  typeof AlertDialogPrimitive.Root
>

const AlertDialog = AlertDialogPrimitive.Root

const AlertDialogTrigger = AlertDialogPrimitive.Trigger

const AlertDialogPortal = AlertDialogPrimitive.Portal

function AlertDialogOverlay({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-40 bg-black/80',
        className,
      )}
      {...props}
      ref={ref}
    />
  )
}

function AlertDialogContent({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content>) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(
          'bg-tremor-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-40 flex w-full max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col gap-4 border p-6 shadow-lg duration-200 md:rounded-lg',
          className,
        )}
        {...props}
      />
    </AlertDialogPortal>
  )
}

export type AlertDialogHeaderProps = ComponentProps<'div'>

function AlertDialogHeader({ className, ...props }: AlertDialogHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-col space-y-2 text-center md:text-left',
        className,
      )}
      {...props}
    />
  )
}

export type AlertDialogFooterProps = ComponentProps<'div'>

function AlertDialogFooter({ className, ...props }: AlertDialogFooterProps) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-y-1.5 md:flex-row md:items-center md:justify-end md:gap-x-2',
        className,
      )}
      {...props}
    />
  )
}

function AlertDialogTitle({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      ref={ref}
      className={cn('text-lg font-semibold', className)}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      ref={ref}
      className={cn('text-tremor-content text-sm', className)}
      {...props}
    />
  )
}

function AlertDialogAction({
  ref,
  variant = 'outline',
  ...props
}: Omit<ButtonProps, 'asChild'>) {
  return (
    <AlertDialogPrimitive.Action ref={ref} asChild>
      <Button variant={variant} {...props} />
    </AlertDialogPrimitive.Action>
  )
}

function AlertDialogCancel({
  ref,
  variant = 'outline',
  ...props
}: Omit<ButtonProps, 'asChild'>) {
  return (
    <div className="mt-2 md:mt-0">
      <AlertDialogPrimitive.Cancel ref={ref} asChild>
        <Button variant={variant} {...props} />
      </AlertDialogPrimitive.Cancel>
    </div>
  )
}

export {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
}
